import type { ImmutableMap } from "@suse-o11y/immutable-map";
import { toArray } from "@suse-o11y/to-array";

import { bottom, compactItem } from "~/components/grid-layout/item";

import { type Setter, resolve } from "../../generic/setter";

import { DashboardPanelSpec, type DashboardPanelSpecConfig } from "./_dashboard-panel-spec";

export class DashboardPanelsSpec {
	public constructor(
		private readonly panels: ImmutableMap<string, DashboardPanelSpec>,
		public readonly cols: number = 24,
	) {}

	public addPanel(newPanel: DashboardPanelSpec): DashboardPanelsSpec {
		const panelsLayout = this.panels.toArray().map(([_id, panel]) => ({
			x: panel.layout.x,
			y: panel.layout.y,
			w: panel.layout.width,
			h: panel.layout.height,
			i: panel.id,
			static: true,
		}));

		const { x, y } = compactItem({
			layout: panelsLayout,
			item: {
				x: newPanel.layout.x,
				y: bottom(panelsLayout),
				w: newPanel.layout.width,
				h: newPanel.layout.height,
				i: newPanel.id,
			},
			cols: this.cols,
			compactType: "vertical",
		});

		return new DashboardPanelsSpec(
			this.panels.set(
				newPanel.id,
				newPanel.update((panel) => ({ ...panel, layout: panel.layout.update({ x, y }) })),
			),
		);
	}

	public updatePanel(
		id: string,
		setter: Setter<Partial<Omit<DashboardPanelSpecConfig, "id">>, [DashboardPanelSpec]>,
	): DashboardPanelsSpec {
		const panel = this.panels.get(id);

		if (!panel) {
			return this;
		}

		const { layout = panel.layout, widget = panel.widget } = resolve(setter, panel);

		const updated = new DashboardPanelSpec(id, layout, widget);

		if (updated.isEqual(panel)) {
			return this;
		}

		return new DashboardPanelsSpec(this.panels.set(id, updated));
	}

	public deletePanel(key: string): DashboardPanelsSpec {
		return new DashboardPanelsSpec(this.panels.delete(key));
	}

	public toArray(): Array<[string, DashboardPanelSpec]> {
		return toArray(this.panels);
	}

	public isEqual(another: DashboardPanelsSpec): boolean {
		return this.panels.isEqual(another.panels);
	}

	public values(): IterableIterator<DashboardPanelSpec> {
		return this.panels.values();
	}

	public get size(): number {
		return this.panels.size;
	}
}
